import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import HTMLFlipBook from "react-pageflip";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { API, useAuth } from "@/App";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import SiteHeader from "@/components/SiteHeader";
import CatalogNavDropdown from "@/components/CatalogNavDropdown";
import MarketplaceSearchBar from "@/components/MarketplaceSearchBar";
import {
  Search, ShoppingCart, ChevronDown, ArrowLeft, Home, X, Plus, Minus,
  Phone, Send, Loader2, CheckCircle2, Package, Trash2,
  BookOpen, Upload, ChevronLeft, ChevronRight, Download,
  FileText, Info, Shield, Tag, Share2, Link2, Mail,
  Truck, Brain, Sparkles, Handshake, Clock, Zap, Stethoscope
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_DETAIL_CACHE = {};

async function fetchProductDetail(productId) {
  if (PRODUCT_DETAIL_CACHE[productId]) return PRODUCT_DETAIL_CACHE[productId];
  const r = await fetch(`${API}/surgical/products/${productId}`);
  if (!r.ok) throw new Error("Product not found");
  const data = await r.json();
  PRODUCT_DETAIL_CACHE[productId] = data;
  return data;
}

function sanitizeHtml(html) {
  // Strip <script>/<style> tags — Aspen content is marketing copy with <ul>/<li>/<sup>
  return (html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/on\w+="[^"]*"/gi, "");
}

const HERO_SLIDES = [
  {
    eyebrow: "A Distribution Network Without Equal",
    headline: "Never Left Without What You Need",
    body: "From single-use blades to complex laparoscopic instrumentation — our multi-hub distribution network keeps 2,000+ surgical SKUs moving to your OR, day or night.",
    cta: "Browse the Catalog",
    ctaHref: "#products",
    icon: Truck,
    accent: "from-cyan-600/20 via-blue-600/10 to-transparent",
    image: "https://cdn.brandfolder.io/TVDZACS7/at/6fq3vzgvzwqm56r3rksf3s/Laparoscopic_Products_Drawing.png",
  },
  {
    eyebrow: "Powered by the Sterile Mate Intelligence Platform",
    headline: "Smart Sourcing. Real-Time Visibility.",
    body: "Every SKU tracked, every shortage predicted. Our intelligence engine routes your order to the nearest in-stock hub before a backorder can even happen.",
    cta: "See How It Works",
    ctaHref: "/solutions",
    icon: Brain,
    accent: "from-violet-600/20 via-indigo-600/10 to-transparent",
    image: "https://cdn.brandfolder.io/TVDZACS7/at/3zb8ggxx7qwcgbjzcshf7vm/Electrosurgery_Drawing.png",
  },
  {
    eyebrow: "5 Years Solving Your Backorders",
    headline: "Now Launching a Full Surgical Line",
    body: "After half a decade making the impossible sourceable, we're bringing you a native catalog — built with our trusted partners at Aspen Medical and BluBox.",
    cta: "Read Our Story",
    ctaHref: "#about-surgical",
    icon: Sparkles,
    accent: "from-amber-500/20 via-orange-500/10 to-transparent",
    image: "https://cdn.brandfolder.io/TVDZACS7/at/mfp9sctpx6hbxj7pvsk4pmc/Sterile_Processing_Drawing.png",
  },
  {
    eyebrow: "Partners Who Actually Deliver",
    headline: "Aspen Medical + BluBox, United Under Sterile Mate",
    body: "Access a curated portfolio of surgical essentials, infection control, sterile processing and more — from the brands your surgeons already ask for.",
    cta: "Meet Our Partners",
    ctaHref: "#about-surgical",
    icon: Handshake,
    accent: "from-emerald-600/20 via-teal-600/10 to-transparent",
    image: "https://cdn.brandfolder.io/TVDZACS7/at/n35rbvtpf578c8w37rk3vq/Blades_and_Scalpels_Drawing.png",
  },
  {
    eyebrow: "Quote in Under 2 Hours",
    headline: "Add. Submit. Ship.",
    body: "Build a quote cart, drop your shipping address, and our team responds with pricing before your next coffee break. No reps, no voicemail, no runaround.",
    cta: "Start a Quote",
    ctaHref: "#products",
    icon: Zap,
    accent: "from-rose-600/20 via-pink-600/10 to-transparent",
    image: "https://cdn.brandfolder.io/TVDZACS7/at/q4pj44c84ss5sj2w86nv9jhk/Wound_Care_Drawing.png",
  },
];

export default function SurgicalCatalog() {
  const [search, setSearch] = useState("");
  const [selectedTop, setSelectedTop] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Shared cart context — same cart as Sol-M pages
  const { cart, setShowCart, addItem, showCart } = useQuoteCart();

  // Product detail modal
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Hero slideshow
  const [slide, setSlide] = useState(0);
  const [slidePaused, setSlidePaused] = useState(false);
  useEffect(() => {
    if (slidePaused) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [slidePaused]);

  // Flipbook
  const { user, token } = useAuth() || {};
  const isAdmin = user && (user.role === "super_admin" || user.role === "hub_admin");
  const [catalogPdf, setCatalogPdf] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [flipPage, setFlipPage] = useState(0);
  const [showFlipbook, setShowFlipbook] = useState(false);
  const [uploading, setUploading] = useState(false);
  const flipbookRef = useRef(null);
  const [flipDims, setFlipDims] = useState({ width: 420, height: 560 });

  const computeFlipDims = () => {
    if (typeof window === "undefined") return { width: 420, height: 560 };
    const maxW = Math.min(window.innerWidth - 80, 1100);
    const pageW = Math.floor(maxW / 2);
    const pageH = Math.floor(pageW * 1.35);
    const capH = Math.min(pageH, window.innerHeight - 260);
    const finalW = Math.floor(capH / 1.35);
    return { width: Math.min(pageW, finalW), height: Math.min(pageH, capH) };
  };

  useEffect(() => {
    setFlipDims(computeFlipDims());
    const onResize = () => setFlipDims(computeFlipDims());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load catalog from backend
  useEffect(() => {
    (async () => {
      try {
        setCatalogLoading(true);
        const r = await fetch(`${API}/surgical/catalog`);
        const data = await r.json();
        setCatalog(data.categories || []);
      } catch {
        toast.error("Failed to load catalog");
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  // Load flipbook PDF
  const loadCatalogPdf = useCallback(async () => {
    try {
      const r = await fetch(`${API}/catalog-pdf`);
      const data = await r.json();
      if (data.catalog) { setCatalogPdf(data.catalog); setPdfUrl(`${BACKEND}${data.url}`); }
      else { setCatalogPdf(null); setPdfUrl(null); }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    loadCatalogPdf();
    if (typeof window !== "undefined" && window.location.hash === "#flipbook") setShowFlipbook(true);
  }, [loadCatalogPdf]);

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please upload a PDF"); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("Max 50MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.pdf$/i, ""));
    try {
      const r = await fetch(`${API}/admin/catalog-pdf`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error();
      toast.success("Catalog uploaded");
      await loadCatalogPdf();
      setShowFlipbook(true);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  // Cart
  const addToCart = (product) => addItem({
    erp: product.erp, name: product.name, image: product.image,
    source: "Aspen Surgical",
  });

  // Product detail
  const openDetail = async (productSummary) => {
    setDetailLoading(true);
    setDetailProduct({ ...productSummary, _loading: true });
    // Update URL so the link is shareable (without forcing a full navigation)
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("product", productSummary.id);
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
    try {
      const { product, category } = await fetchProductDetail(productSummary.id);
      setDetailProduct({ ...product, category });
    } catch {
      toast.error("Could not load product details");
      setDetailProduct(null);
    } finally { setDetailLoading(false); }
  };

  const closeDetail = () => {
    setDetailProduct(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("product");
      window.history.replaceState({}, "", url.toString());
    } catch { /* noop */ }
  };

  // Deep link: open product modal when URL has ?product=<id>
  useEffect(() => {
    if (!catalog.length) return;
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("product");
    if (pid && !detailProduct) {
      const found = catalog.flatMap(t => t.subs).flatMap(s => s.products).find(p => p.id === pid || p.erp === pid);
      if (found) openDetail(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  // Share helpers
  const getShareUrl = (product) => {
    const base = `${window.location.origin}/sterilemate/surgical`;
    return `${base}?product=${encodeURIComponent(product.id || product.erp)}`;
  };

  const shareViaEmail = (product) => {
    const url = getShareUrl(product);
    const subject = `Sterile Mate — ${product.name}${product.erp ? ` (SKU: ${product.erp})` : ""}`;
    const bodyLines = [
      `Take a look at this product from the Sterile Mate Surgical Marketplace:`,
      ``,
      product.name,
      product.erp ? `SKU: ${product.erp}` : "",
      product.brand ? `Brand: ${product.brand}` : "",
      ``,
      product.description || "",
      ``,
      `View full details & request a quote:`,
      url,
      ``,
      `— Sterile Mate  ·  281.456.3458  ·  orders@sterilemate.com`,
    ].filter(Boolean).join("\n");
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
    window.location.href = mailto;
  };

  const copyShareLink = async (product) => {
    const url = getShareUrl(product);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      // Fallback: show the URL
      toast.message("Share link", { description: url });
    }
  };

  const nativeShare = async (product) => {
    const url = getShareUrl(product);
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `${product.name} — ${product.description || "Sterile Mate Surgical Marketplace"}`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      copyShareLink(product);
    }
  };

  // Filter + display
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q && !selectedTop && !selectedSub) return catalog;
    return catalog
      .map(top => {
        if (selectedTop && top.slug !== selectedTop) return null;
        const subs = top.subs
          .map(sub => {
            if (selectedSub && sub.slug !== selectedSub) return null;
            const products = q
              ? sub.products.filter(p =>
                  (p.name || "").toLowerCase().includes(q) ||
                  (p.erp || "").toLowerCase().includes(q) ||
                  (p.description || "").toLowerCase().includes(q) ||
                  (p.brand || "").toLowerCase().includes(q))
              : sub.products;
            return products.length ? { ...sub, products } : null;
          }).filter(Boolean);
        return subs.length ? { ...top, subs } : null;
      }).filter(Boolean);
  }, [catalog, search, selectedTop, selectedSub]);

  const totalProducts = catalog.reduce((s, t) => s + t.subs.reduce((ss, sb) => ss + sb.products.length, 0), 0);

  return (
    <div className="min-h-screen bg-white" data-testid="surgical-catalog-page">
      <SiteHeader />
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] via-[#0056A8] to-[#003366] border-b border-blue-500/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 transition-colors"
                data-testid="surgical-back-btn">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 transition-colors"
                data-testid="surgical-home-btn">
                <Home className="w-3.5 h-3.5" /> Home
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em] mb-1">Sterile Mate Marketplace</p>
              <h1 className="text-3xl sm:text-4xl font-black text-white">Surgical Products</h1>
              <p className="text-blue-200 mt-1">
                {catalogLoading ? "Loading catalog..." : `${totalProducts.toLocaleString()} products across ${catalog.length} categories`}
              </p>
            </div>
            <button onClick={() => setShowCart(true)} className="relative bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-5 py-3 text-white flex items-center gap-2" data-testid="open-cart-btn">
              <ShoppingCart className="w-5 h-5" />
              <span className="font-semibold">Quote Cart</span>
              {cart.length > 0 && <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{cart.length}</span>}
            </button>
            <CatalogNavDropdown />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ===== AMERICAN SURGICAL AUTHORIZED-DISTRIBUTOR BANNER ===== */}
        <Link
          to="/marketplace/american-surgical"
          className="group relative flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 mb-8 p-5 rounded-2xl bg-gradient-to-r from-blue-900 via-blue-800 to-slate-800 border border-amber-400/30 hover:border-amber-400/60 shadow-lg overflow-hidden transition-all"
          data-testid="asc-announcement-banner"
        >
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
          <div className="relative flex items-center gap-3 shrink-0">
            <span className="px-2.5 py-1 bg-amber-400 text-slate-900 text-[10px] font-black uppercase tracking-widest rounded shadow-sm">New</span>
            <span className="text-cyan-300 text-xs font-bold uppercase tracking-widest hidden sm:inline">Authorized Distributor</span>
          </div>
          <div className="relative flex-1">
            <p className="text-white font-black text-base sm:text-lg leading-snug">
              Sterile Mate is now an <span className="text-amber-300">Authorized Distributor</span> for American Surgical — industry-leading pricing on Neurosurgical Patties, Sponges &amp; more.
            </p>
            <p className="text-blue-200 text-xs sm:text-sm mt-1">Pre-orders open for Q3. Direct-from-manufacturer pricing, no third-party markup.</p>
          </div>
          <span className="relative inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-amber-400 group-hover:bg-amber-300 text-slate-900 text-sm font-bold shadow-md transition-colors shrink-0">
            View Catalog &amp; Pre-Order <ChevronRight className="w-4 h-4" />
          </span>
        </Link>

        {/* ===== HERO SLIDESHOW ===== */}
        <div
          className="relative overflow-hidden rounded-2xl mb-8 border border-slate-800 bg-[#0b1728]"
          style={{ minHeight: 360 }}
          onMouseEnter={() => setSlidePaused(true)}
          onMouseLeave={() => setSlidePaused(false)}
          data-testid="surgical-hero-slideshow"
        >
          {HERO_SLIDES.map((s, i) => {
            const Icon = s.icon;
            const active = i === slide;
            return (
              <div
                key={i}
                className={`absolute inset-0 grid md:grid-cols-[1.25fr_1fr] gap-8 p-8 md:p-12 transition-opacity duration-700 ${active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
                aria-hidden={!active}
                data-testid={`hero-slide-${i}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} pointer-events-none`} />
                <div className="relative flex flex-col justify-center max-w-xl">
                  <div className="inline-flex items-center gap-2 text-cyan-300 font-semibold text-xs uppercase tracking-[0.18em] mb-4">
                    <Icon className="w-4 h-4" /> {s.eyebrow}
                  </div>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-[1.05] mb-4">
                    {s.headline}
                  </h2>
                  <p className="text-slate-300 text-base md:text-lg mb-6 max-w-lg">{s.body}</p>
                  <div>
                    {s.ctaHref.startsWith("/") ? (
                      <Link to={s.ctaHref}>
                        <Button className="bg-white text-slate-900 hover:bg-slate-200 h-11 px-6 font-semibold">
                          {s.cta} <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    ) : (
                      <a href={s.ctaHref}>
                        <Button className="bg-white text-slate-900 hover:bg-slate-200 h-11 px-6 font-semibold">
                          {s.cta} <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
                <div className="relative hidden md:flex items-center justify-center">
                  {s.image && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-8 w-full h-full flex items-center justify-center backdrop-blur-sm">
                      <img src={s.image} alt="" className="max-h-[280px] object-contain drop-shadow-2xl" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`transition-all ${i === slide ? "w-8 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"} rounded-full`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`slide-dot-${i}`}
              />
            ))}
          </div>

          {/* Arrows */}
          <button
            onClick={() => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur border border-white/10 flex items-center justify-center text-white"
            aria-label="Previous slide"
            data-testid="slide-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSlide((s) => (s + 1) % HERO_SLIDES.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur border border-white/10 flex items-center justify-center text-white"
            aria-label="Next slide"
            data-testid="slide-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Flipbook section — placed at top as the hero element */}
        <div id="flipbook" className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8" data-testid="flipbook-section">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center"><BookOpen className="w-6 h-6 text-blue-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{catalogPdf ? catalogPdf.title : "Flipbook Catalog"}</h3>
                <p className="text-sm text-slate-600">
                  {catalogPdf ? `${numPages || "..."} pages · Flip through like a real brochure` : "Upload a PDF to display an interactive flipbook"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {catalogPdf && (
                <>
                  <Button onClick={() => setShowFlipbook(!showFlipbook)} variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-50" data-testid="toggle-flipbook-btn">
                    {showFlipbook ? "Hide Flipbook" : "Open Flipbook"}
                  </Button>
                  <a href={pdfUrl} target="_blank" rel="noreferrer" download>
                    <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"><Download className="w-4 h-4 mr-1" /> PDF</Button>
                  </a>
                </>
              )}
              {isAdmin && (
                <label className="cursor-pointer">
                  <input type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} disabled={uploading} data-testid="admin-upload-pdf-input" />
                  <span className={`inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm font-semibold ${uploading ? "bg-slate-600" : "bg-amber-600 hover:bg-amber-700"} text-white`}>
                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> {catalogPdf ? "Replace PDF" : "Upload PDF"}</>}
                  </span>
                </label>
              )}
            </div>
          </div>

          {showFlipbook && pdfUrl && (
            <div className="flex flex-col items-center bg-slate-900/60 rounded-xl p-4 border border-slate-700 mt-4" data-testid="flipbook-viewer">
              <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={<div className="py-20 text-slate-400 flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>}>
                {numPages > 0 && (
                  <HTMLFlipBook width={flipDims.width} height={flipDims.height} size="fixed"
                    minWidth={200} maxWidth={1200} minHeight={250} maxHeight={1400}
                    showCover mobileScrollSupport onFlip={(e) => setFlipPage(e.data)} ref={flipbookRef} className="mx-auto">
                    {Array.from({ length: numPages }, (_, i) => (
                      <div key={i} className="bg-white shadow-xl" style={{ width: flipDims.width, height: flipDims.height }}>
                        <Page pageNumber={i + 1} width={flipDims.width} renderAnnotationLayer={false} renderTextLayer={false}
                          loading={<div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>} />
                      </div>
                    ))}
                  </HTMLFlipBook>
                )}
              </Document>
              {numPages > 0 && (
                <div className="flex items-center gap-4 mt-4 bg-slate-800/70 rounded-lg px-4 py-2">
                  <button onClick={() => flipbookRef.current?.pageFlip()?.flipPrev()} className="p-1.5 hover:bg-slate-700 rounded" data-testid="flipbook-prev"><ChevronLeft className="w-4 h-4 text-white" /></button>
                  <span className="text-xs text-slate-300 font-medium" data-testid="flipbook-page-indicator">Page {Math.min(flipPage + 1, numPages)} / {numPages}</span>
                  <button onClick={() => flipbookRef.current?.pageFlip()?.flipNext()} className="p-1.5 hover:bg-slate-700 rounded" data-testid="flipbook-next"><ChevronRight className="w-4 h-4 text-white" /></button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Unified search — Surgical + all Sol-M */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 max-w-2xl">
            <MarketplaceSearchBar placeholder="Search all products by name, SKU, or item number..." />
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter this page..." className="pl-11 bg-white border-slate-300 text-slate-900 h-10" data-testid="catalog-filter" />
          </div>
          {(selectedTop || selectedSub) && (
            <Button variant="outline" onClick={() => { setSelectedTop(null); setSelectedSub(null); }} className="border-slate-300 text-slate-700 hover:bg-slate-100">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Top categories */}
        {!catalogLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="category-grid">
            {catalog.map((cat) => (
              <button key={cat.slug}
                onClick={() => { setSelectedTop(selectedTop === cat.slug ? null : cat.slug); setSelectedSub(null); }}
                className={`rounded-xl p-3 text-center transition-all hover:-translate-y-0.5 border ${selectedTop === cat.slug ? "bg-[#0056A8] border-blue-500 shadow-lg text-white" : "bg-white border-slate-200 hover:border-slate-400 hover:shadow-sm"}`}>
                {cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-16 object-contain mb-2 bg-white rounded" loading="lazy" />
                  : <div className="w-full h-16 mb-2 bg-slate-100 rounded flex items-center justify-center"><Package className="w-6 h-6 text-slate-400" /></div>}
                <p className={`text-xs font-medium leading-tight ${selectedTop === cat.slug ? "text-white" : "text-slate-800"}`}>{cat.name}</p>
              </button>
            ))}
          </div>
        )}

        {/* Sub-category chips when a top category is selected */}
        {selectedTop && !catalogLoading && (
          <div className="flex flex-wrap gap-2 mb-8">
            {catalog.find(c => c.slug === selectedTop)?.subs.map(sub => (
              <button key={sub.slug}
                onClick={() => setSelectedSub(selectedSub === sub.slug ? null : sub.slug)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedSub === sub.slug ? "bg-blue-50 border-blue-500 text-blue-800" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                {sub.name} <span className="text-slate-400 ml-1">({sub.products.length})</span>
              </button>
            ))}
          </div>
        )}

        {/* Products */}
        <div id="products" className="scroll-mt-20" />
        {catalogLoading ? (
          <div className="flex items-center justify-center py-32 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading 2,000+ surgical products...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-slate-700 font-medium">No matches</h3>
            <p className="text-slate-500 text-sm mt-1">Try a different search or clear filters</p>
          </div>
        ) : (
          filtered.map((top) => (
            <div key={top.slug} className="mb-10">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                {top.image && <img src={top.image} alt="" className="w-8 h-8 object-contain bg-white rounded p-1" />}
                {top.name}
              </h2>
              {top.subs.map((sub) => (
                <div key={sub.slug} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-800">{sub.name}</span>
                    <span className="text-xs text-slate-500">({sub.products.length})</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 pl-6">
                    {sub.products.map((p) => (
                      <div key={p.id}
                        className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group relative"
                        data-testid={`product-${p.erp}`}
                        onClick={() => openDetail(p)}>
                        <button
                          onClick={(e) => { e.stopPropagation(); shareViaEmail(p); }}
                          className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/95 hover:bg-blue-600 border border-slate-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                          title="Email this product"
                          data-testid={`card-share-${p.erp}`}>
                          <Mail className="w-3.5 h-3.5 text-slate-700 hover:text-white" />
                        </button>
                        <div className="bg-slate-50 p-3 flex items-center justify-center h-32">
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                          ) : <Package className="w-10 h-10 text-slate-300" />}
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] text-slate-500 font-mono truncate">{p.erp}</p>
                          <p className="text-sm font-semibold text-slate-900 mt-0.5 leading-tight line-clamp-2">{p.name}</p>
                          {p.brand && <p className="text-xs text-blue-700 mt-0.5">{p.brand}</p>}
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description}</p>
                          {p.variant_count > 0 && <p className="text-[10px] text-amber-700 mt-1 font-semibold">{p.variant_count} sizes/options</p>}
                          <button
                            onClick={(e) => { e.stopPropagation(); addToCart({ erp: p.erp, name: p.name, image: p.image }); }}
                            className="mt-3 w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5"
                            data-testid={`add-${p.erp}`}>
                            <Plus className="w-3.5 h-3.5" /> Add to Quote
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        {/* ===== SOL-M AUTHORIZED DISTRIBUTOR CATEGORIES ===== */}
        <section className="my-10 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 p-8 md:p-10" data-testid="solm-nav-section">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-[0.18em] mb-1">Authorized Distributor</p>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900">Beyond the OR — Full Sol-M Portfolio</h2>
              <p className="text-sm text-slate-600 mt-1 max-w-xl">
                Same quote cart, same 2-hour response. Jump into any Sol-M category below — items add to the
                same cart as your surgical products.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[
              { slug: "diabetes-care", name: "Diabetes Care" },
              { slug: "medication-delivery", name: "Medication Delivery" },
              { slug: "blood-collection", name: "Blood Collection" },
              { slug: "wound-care", name: "Wound Care" },
              { slug: "ppe", name: "PPE" },
              { slug: "vascular-access", name: "Vascular Access & Infusion" },
              { slug: "incontinence", name: "Incontinence" },
              { slug: "bulk-non-sterile", name: "Bulk Non-Sterile" },
            ].map((c) => (
              <Link
                key={c.slug}
                to={`/sterilemate/${c.slug}`}
                className="group bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition-all hover:-translate-y-0.5"
                data-testid={`surgical-to-solm-${c.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{c.name}</p>
                    <p className="text-xs text-slate-500">Sol-M authorized</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-700" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ===== BEAR-ENT ENT CATEGORIES ===== */}
        <section className="my-10 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 p-8 md:p-10" data-testid="ent-nav-section">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-[0.18em] mb-1">ENT Partner · BEAR-ENT</p>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900">ENT — Ear, Nose & Throat Instruments</h2>
              <p className="text-sm text-slate-600 mt-1 max-w-xl">
                German stainless-steel reusable instruments for otology, rhinology, and laryngeal procedures.
                Quote any item — items drop into the same cart as your surgical and Sol-M products.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { slug: "ent-ear", name: "Ear Instruments (Otology)", hint: "Baron suctions, specula, forceps, curettes" },
              { slug: "ent-nose", name: "Nasal & Sinus (Rhinology)", hint: "Nasal specula, polyp forceps, elevators" },
              { slug: "ent-throat", name: "Throat (Laryngeal)", hint: "Mirrors, tongue depressors, adenoid punches" },
              { slug: "ent-office-set", name: "Office Instrument Set", hint: "Curated diagnostic + minor-procedure set" },
              { slug: "ent-surgical", name: "Surgical Instrument Set", hint: "OR/ASC forceps, scissors, needle holders" },
              { slug: "ent-general", name: "General Instruments", hint: "Stitch scissors, tongue depressors, utility" },
            ].map((c) => (
              <Link
                key={c.slug}
                to={`/sterilemate/${c.slug}`}
                className="group bg-white border border-slate-200 rounded-xl p-4 hover:border-purple-400 hover:shadow-md transition-all hover:-translate-y-0.5"
                data-testid={`surgical-to-${c.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 text-purple-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-purple-700 transition-colors">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.hint}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-purple-700" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ===== ABOUT OUR SURGICAL ===== */}
        <section id="about-surgical" className="scroll-mt-20 my-10 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm" data-testid="about-surgical-section">
          <div className="grid md:grid-cols-[1.1fr_0.9fr]">
            <div className="p-8 md:p-12">
              <div className="inline-flex items-center gap-2 text-amber-700 font-semibold text-xs uppercase tracking-[0.18em] mb-4">
                <Sparkles className="w-4 h-4" /> About Our Surgical Line
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-5">
                Five Years of Fixing Backorders. Now Offering the Products Themselves.
              </h2>
              <div className="text-slate-700 text-base leading-relaxed space-y-4 max-w-xl">
                <p>
                  For the past five years, Sterile Mate has been the team hospitals and surgery centers call
                  when their primary distributor says <em>&ldquo;out of stock — check back in six weeks.&rdquo;</em> We
                  tracked inventory no one else could see. We moved product when others couldn&apos;t. We built a
                  <strong className="text-slate-900"> nationwide intelligence platform</strong> that knows where
                  every critical SKU is sitting — right now.
                </p>
                <p>
                  Along the way we learned what your ORs actually use, which brands your surgeons trust, and
                  where the supply chain quietly breaks. So this felt like the natural next move:
                  <strong className="text-slate-900"> launch a full surgical product line</strong> built on top
                  of that same intelligence.
                </p>
                <p>
                  We didn&apos;t do it alone. Our catalog is curated with our share partners at
                  <strong className="text-blue-700"> Aspen Medical</strong> and
                  <strong className="text-blue-700"> BluBox</strong> — two names respected across
                  perioperative care, infection control, sterile processing, and surgical essentials.
                  Together we bring you best-in-class product at distributor prices, with the same visibility
                  you&apos;ve always gotten from us.
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center mb-2">
                    <Clock className="w-5 h-5 text-blue-700" />
                  </div>
                  <p className="text-2xl font-black text-slate-900">5 Years</p>
                  <p className="text-xs text-slate-600">Solving backorders before this launch</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="w-10 h-10 rounded-lg bg-violet-50 border border-violet-200 flex items-center justify-center mb-2">
                    <Brain className="w-5 h-5 text-violet-700" />
                  </div>
                  <p className="text-2xl font-black text-slate-900">2,000+</p>
                  <p className="text-xs text-slate-600">SKUs tracked in real time</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center mb-2">
                    <Handshake className="w-5 h-5 text-amber-700" />
                  </div>
                  <p className="text-2xl font-black text-slate-900">2 Partners</p>
                  <p className="text-xs text-slate-600">Aspen Medical · BluBox</p>
                </div>
              </div>
            </div>

            <div className="relative bg-slate-50 border-l border-slate-200 p-8 md:p-10 flex flex-col justify-center">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Our Share Partners</h3>
              <div className="space-y-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-blue-700" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Aspen Medical</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    A trusted portfolio of perioperative brands — Bard-Parker®, Protek™, Richard-Allan™ and
                    more — covering blades, electrosurgery, laparoscopic, wound care, and sterile processing.
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                      <Package className="w-5 h-5 text-emerald-700" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">BluBox</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Infection control, PPE, and single-use surgical essentials delivered through a
                    streamlined logistics network built for hospitals and ambulatory surgery centers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-r from-[#003366] to-[#0056A8] rounded-xl p-8 border border-blue-500/20 text-center mt-8">
          <h3 className="text-2xl font-bold text-white mb-2">Need Pricing?</h3>
          <p className="text-blue-200 mb-6">Add products to your Quote Cart and submit. We respond within 2 hours.</p>
          <div className="flex items-center justify-center gap-4">
            <Button onClick={() => setShowCart(true)} className="bg-amber-500 hover:bg-amber-600 text-white"><ShoppingCart className="w-4 h-4 mr-2" /> Open Quote Cart ({cart.length})</Button>
            <a href="tel:2814563458"><Button variant="outline" className="border-white/40 text-white hover:bg-white/10"><Phone className="w-4 h-4 mr-2" /> 281.456.3458</Button></a>
          </div>
        </div>
      </div>

      {/* Product Detail Dialog */}
      <Dialog open={!!detailProduct} onOpenChange={(v) => { if (!v) setDetailProduct(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
          {detailLoading || (detailProduct && detailProduct._loading) ? (
            <div className="py-20 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading product details...
            </div>
          ) : detailProduct && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-2 mb-1">
                  {detailProduct.brand && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-0.5">
                      <Tag className="w-3 h-3" /> {detailProduct.brand}
                    </span>
                  )}
                  {detailProduct.category && (
                    <span className="text-xs text-slate-500">{detailProduct.category.top} / {detailProduct.category.sub}</span>
                  )}
                </div>
                <DialogTitle className="text-2xl" data-testid="product-detail-title">{detailProduct.name}</DialogTitle>
                <p className="text-sm font-mono text-slate-500">SKU: {detailProduct.erp}</p>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                {/* Left: images */}
                <div>
                  {detailProduct.images && detailProduct.images[0] ? (
                    <div className="bg-white rounded-xl p-6 border border-slate-200 flex items-center justify-center min-h-[260px]">
                      <img src={detailProduct.images[0].large || detailProduct.images[0].medium}
                        alt={detailProduct.name} className="max-w-full max-h-[340px] object-contain" />
                    </div>
                  ) : (
                    <div className="bg-slate-100 rounded-xl p-6 flex items-center justify-center min-h-[260px]">
                      <Package className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                  {detailProduct.images && detailProduct.images.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto">
                      {detailProduct.images.map((img, i) => (
                        <img key={i} src={img.small || img.medium} alt="" className="w-16 h-16 object-contain bg-white rounded border border-slate-200 flex-shrink-0" />
                      ))}
                    </div>
                  )}
                </div>

                {/* Right: summary */}
                <div>
                  {detailProduct.description && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Description</h4>
                      <p className="text-sm text-slate-700 leading-relaxed">{detailProduct.description}</p>
                    </div>
                  )}

                  {detailProduct.attributes && detailProduct.attributes.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> At a Glance</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {detailProduct.attributes.map((a, i) => (
                          <div key={i} className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">{a.name}</p>
                            <p className="text-sm font-medium text-slate-800">{(a.values || []).join(", ") || "—"}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {detailProduct.unit_of_measure && (
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold">Unit of Measure:</span> {detailProduct.unit_of_measure}
                      {detailProduct.pack_description && ` · ${detailProduct.pack_description}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Specifications */}
              {detailProduct.specifications && detailProduct.specifications.length > 0 && (
                <div className="mt-6 border-t pt-6">
                  {detailProduct.specifications.map((spec, i) => (
                    <div key={i} className="mb-5">
                      <h4 className="font-bold text-slate-800 mb-2">{spec.name}</h4>
                      {spec.html ? (
                        <div className="prose prose-sm prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(spec.html) }} />
                      ) : spec.value ? (
                        <p className="text-sm text-slate-700">{spec.value}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {/* Variants */}
              {detailProduct.variants && detailProduct.variants.length > 0 && (
                <div className="mt-6 border-t pt-6">
                  <h4 className="font-bold text-slate-800 mb-3">Available Sizes / SKUs ({detailProduct.variants.length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {detailProduct.variants.map((v) => (
                      <div key={v.erp} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2 border border-slate-200">
                        {v.image ? <img src={v.image} alt="" className="w-12 h-12 object-contain bg-white rounded" />
                          : <div className="w-12 h-12 bg-white rounded flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono text-slate-500">{v.erp}</p>
                          <p className="text-sm text-slate-800 truncate">{v.name.split(" - ").slice(-1)[0] || v.name}</p>
                        </div>
                        <button
                          onClick={() => addToCart({ erp: v.erp, name: v.name, image: v.image })}
                          className="text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold px-2.5 py-1 rounded" data-testid={`variant-add-${v.erp}`}>
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {detailProduct.documents && detailProduct.documents.length > 0 && (
                <div className="mt-6 border-t pt-6">
                  <h4 className="font-bold text-slate-800 mb-3">Documents</h4>
                  <div className="flex flex-wrap gap-2">
                    {detailProduct.documents.map((d, i) => (
                      <a key={i} href={d.url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-100">
                        <FileText className="w-3.5 h-3.5" /> {d.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 pt-6 border-t flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={() => addToCart({ erp: detailProduct.erp, name: detailProduct.name, image: detailProduct.images?.[0]?.medium })}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="detail-add-to-quote">
                    <Plus className="w-4 h-4 mr-2" /> Add to Quote
                  </Button>
                  <Button
                    onClick={() => shareViaEmail(detailProduct)}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="detail-email-share-btn">
                    <Mail className="w-4 h-4 mr-2" /> Email Product
                  </Button>
                  <Button
                    onClick={() => copyShareLink(detailProduct)}
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    data-testid="detail-copy-link-btn">
                    <Link2 className="w-4 h-4 mr-2" /> Copy Link
                  </Button>
                  {typeof navigator !== "undefined" && navigator.share && (
                    <Button
                      onClick={() => nativeShare(detailProduct)}
                      variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      data-testid="detail-native-share-btn">
                      <Share2 className="w-4 h-4 mr-2" /> Share
                    </Button>
                  )}
                </div>
                <Button onClick={closeDetail} variant="outline">Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Cart Dialog — shared across all Sterile Mate marketplace pages */}
    </div>
  );
}
